import { Component, inject, PLATFORM_ID } from '@angular/core';
import WertWidget from '@wert-io/widget-initializer';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { selectedLang } from '../../app.component';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { SwapStatus, wertLangs } from '../../shared/constants/app-enums';
import { PixelService } from '../../services/pixel.service';

@Component({
    selector: 'app-wert-eth',
    templateUrl: './buy-eth-wert.component.html',
    styleUrls: ['./buy-eth-wert.component.scss'],
    standalone: false
})
export class BuyETHWertComponent {
  token: string = environment.tokenName;
  walletAddress!: string;
  isBscChain = false;
  wertWidget!: WertWidget;
  SwapStatus = SwapStatus;
  mountError: boolean = false;
  topupId: string = ''; // Store topup_id for this flow
  currentStep: number = 1;

  private readonly _platformId = inject(PLATFORM_ID);
  readonly modalRef = inject(BsModalRef);
  private readonly http = inject(HttpClient);
  private readonly dataService = inject(PresaleDataService);
  private readonly pixelService = inject(PixelService);

  constructor() {
    // Slight delay allows modal animation to complete before widget mounts
    setTimeout(() => this.showWidget(), 500);
  }

  async showWidget() {
    this.currentStep = 1;
    let data = {
      walletAddress: this.dataService.walletAddress,
      commodity: this.isBscChain ? 'BNB' : 'ETH',
      network: this.isBscChain ? 'bsc' : 'ethereum',
    };

    if (isPlatformBrowser(this._platformId)) {
      this.http.post<{ sessionId: string }>(environment.wertOnRampUrl, data).subscribe(
        (res) => {
          const sessionData = res;
          const wertOptions = {
            partner_id: environment.partnerId,
            session_id: sessionData.sessionId,
            lang: wertLangs[selectedLang] || 'en',
            redirect_url: environment.domain,
            ...data,
          };
          this.wertWidget = new WertWidget({
            ...wertOptions,
            listeners: {
              loaded: () => {
                // Generate unique topup ID for this flow
                this.topupId = this.pixelService.generateTopupId();

                // Track top-up initiated
                this.pixelService.sendPixelEvent('topup_event', {
                  action: 'initiated',
                  topup_id: this.topupId,
                  wallet_address: this.dataService.walletAddress,
                  chain: this.isBscChain ? 'BNB' : 'ETH',
                  chain_id: this.isBscChain ? environment.bsc.chainIdInt : environment.eth.chainIdInt,
                  provider: 'wert',
                  amount_usd_estimated: 0, // Amount not known at this point
                });

                this.modalRef.hide();
              },
              close: () => {},
              position: (_data: { step: string }) => {},
              'payment-status': (status: {
                status: string;
                payment_id: string;
                order_id: string;
                tx_id: string;
              }) => {
                if (status.status == 'success') {
                  this.wertWidget.removeEventListeners();
                  // Track top-up success
                  this.pixelService.sendPixelEvent('topup_event', {
                    action: 'success',
                    topup_id: this.topupId,
                    wallet_address: this.dataService.walletAddress,
                    chain: this.isBscChain ? 'BNB' : 'ETH',
                    chain_id: this.isBscChain ? environment.bsc.chainIdInt : environment.eth.chainIdInt,
                    amount_usd: 0, // Amount not available from Wert
                    tx_hash: status.tx_id || '',
                    source: 'wert',
                  });

                  this.wertWidget.close();
                }
              },
              // Wert error event data shape: { name: string; message: string }
              error: (err: { name: string; message: string }) => {
                // Track top-up failed
                this.pixelService.sendPixelEvent('topup_event', {
                  action: 'failed',
                  topup_id: this.topupId,
                  wallet_address: this.dataService.walletAddress,
                  chain: this.isBscChain ? 'BNB' : 'ETH',
                  chain_id: this.isBscChain ? environment.bsc.chainIdInt : environment.eth.chainIdInt,
                  provider: 'wert',
                  amount_usd_estimated: 0,
                  failure_reason: err?.message || 'Wert top-up failed',
                });
              },
            },
          });
          this.wertWidget.open();
        },
        (_err: unknown) => {
          this.mountError = true;
        },
      );
    }
  }
}
